<template>
    <div class="page-module-list">
        <h3 class="title">{{ mode === "module" ? $t("modules.modules") : $t("modules.relations") }}</h3>
        <div class="table-wrapper">
            <div class="toolbar">
                <div class="left">
                    <a-input-search
                        style="width: 300px; margin: unset !important"
                        v-model="searchText"
                        :placeholder="$t('message.search')"
                        class="search-input"
                        @input="onSearch($event.target.value)"
                    />
                </div>

                <div class="right">
                    <PermissionController :permissions="['table.create']">
                        <SupsisButton primary ghost @click="$feature.beforeCreateTableModule(showCreateModuleModal)"
                            >{{ mode === "module" ? $t("modules.newModule") : $t("modules.newRelation") }}
                        </SupsisButton>
                    </PermissionController>
                </div>
            </div>

            <a-spin :spinning="loading">
                <SupsisTable
                    :columns="getCols()"
                    :rows="filteredModules()"
                    :top-pagination="true"
                    empty-data-bordered
                    :bottom-pagination="true"
                    :pagination-cursor="pagination"
                    :options="{
                        rowStyleOption,
                        clipboardOption,
                        fixedHeader: true,
                        id: 'module-table',
                        borderX: true,
                        borderY: true,
                    }"
                    @cursor-changed="(cursor) => (pagination = cursor)"
                />
            </a-spin>
        </div>

        <Modal2 v-if="isCreateModuleModalVisible" @cancel="hideCreateModuleModal" @ok="createModule" xlarge>
            <template #title>{{ $t("modules.newModule") }}</template>
            <CreateModuleForm ref="createModuleForm" @success="onCreateModuleSuccess" :default-mode="mode" />
        </Modal2>

        <Modal2 v-if="isEditModuleModalVisible" @cancel="hideEditModuleModal" @ok="editModule" xlarge>
            <template #title>{{ mode === "module" ? $t("modules.editModule") : $t("modules.editRelation") }}</template>
            <EditModuleForm
                v-if="mode === 'module'"
                ref="editModuleForm"
                @success="onEditModuleSuccess"
                :module="selectedModule"
            />
            <EditRelationForm v-else ref="editModuleForm" @success="onEditModuleSuccess" :module="selectedModule" />
        </Modal2>
    </div>
</template>

<script>
import Vue from "vue";
import { mapState, mapActions } from "vuex";
import SupsisTable from "../../../components/SupsisTable.vue";
import Modal2 from "../../../components/common/Modal2.vue";
import CreateModuleForm from "./CreateModuleForm.vue";
import EditModuleForm from "./EditModuleForm.vue";
import EditRelationForm from "./EditRelationForm.vue";
import SupsisButton from "../Settings/components/SupsisButton.vue";
import { MODULE_API } from "../../../api/module";
import PermissionController from "../../../components/permission-controller/PermissionController";

export default Vue.extend({
    name: "ModuleList",
    components: {
        SupsisTable,
        Modal2,
        CreateModuleForm,
        EditModuleForm,
        EditRelationForm,
        SupsisButton,
        PermissionController,
    },
    props: {
        mode: {
            type: String,
            default: "module",
        },
    },
    data() {
        return {
            searchText: "",
            pagination: {
                page: 1,
                perPage: 20,
                total: 0,
                defaultPageSize: 50,
                hideOnSinglePage: true,
                pageSizeOptions: [10, 15, 20, 50, 100],
                showQuickJumper: true,
                showSizeChanger: true,
            },
            rowStyleOption: {
                stripe: true,
            },
            clipboardOption: {
                copy: true,
                paste: false,
                cut: false,
                delete: false,
            },
            isCreateModuleModalVisible: false,
            isEditModuleModalVisible: false,
            selectedModule: null,
        };
    },
    computed: {
        ...mapState({
            modules: (state) => state.module.modules,
            loading: (state) => state.module.loading,
            tables: (state) => state.table.tables,
        }),
    },
    methods: {
        ...mapActions({
            syncModules: "module/syncModules",
        }),
        getCols() {
            return [
                {
                    key: "icon",
                    field: "icon",
                    title: this.$t("modules.icon"),
                    width: "80px",
                    align: "center",
                    renderBodyCell: ({ row }, h) => {
                        return row.icon ? (
                            <i class="material-icons" style="font-size: 18px;">
                                {row.icon}
                            </i>
                        ) : (
                            <span>-</span>
                        );
                    },
                },
                {
                    key: "displayName",
                    field: "displayName",
                    title: this.$t("modules.displayName"),
                    width: "",
                    align: "left",
                    renderBodyCell: ({ row }, h) => {
                        return (
                            <a class="module-name-link" onClick={() => this.viewModule(row)}>
                                {row.displayName}
                            </a>
                        );
                    },
                },
                this.mode === "module" && {
                    key: "moduleName",
                    field: "moduleName",
                    title: this.$t("modules.moduleName"),
                    width: "",
                    align: "left",
                },
                this.mode === "module" && {
                    key: "apiName",
                    field: "apiName",
                    title: this.$t("modules.apiName"),
                    width: "",
                    align: "left",
                },
                this.mode === "module" && {
                    key: "type",
                    field: "type",
                    title: this.$t("modules.type"),
                    width: "",
                    align: "left",
                },
                this.mode === "relation" && {
                    key: "relationType",
                    field: "relation.type",
                    title: this.$t("modules.relationType"),
                    width: "",
                    align: "left",
                    renderBodyCell: ({ row }, h) => {
                        const typeMap = {
                            "table-to-table": "Tablodan Tabloya",
                            "table-to-standart": "Tablodan Standart Modüle",
                            "standart-to-table": "Standart Modülden Tabloya",
                        };
                        return <span>{typeMap[row.relation?.type]}</span>;
                    },
                },
                this.mode === "relation" && {
                    key: "from",
                    field: "from",
                    title: "From",
                    width: "",
                    align: "left",
                    renderBodyCell: ({ row }, h) => {
                        if (row.relation?.tableToTable) {
                            const fromTable = row.relation.tableToTable.from;
                            const table = this.tables.find((t) => t._id === fromTable.table);
                            const tableName = table?.name || "-";
                            const fieldName =
                                fromTable.connectionType === "_id"
                                    ? "_id"
                                    : table?.fields?.find((f) => f._id === fromTable.field)?.name || "-";
                            return (
                                <span>
                                    {tableName} ({fieldName})
                                </span>
                            );
                        } else if (row.relation?.standartToTable) {
                            const fromStandart = row.relation.standartToTable.from;
                            return (
                                <span>
                                    {fromStandart.module} ({fromStandart.field})
                                </span>
                            );
                        } else if (row.relation?.tableToStandart) {
                            const fromTable = row.relation.tableToStandart.from;
                            const table = this.tables.find((t) => t._id === fromTable.table);
                            const tableName = table?.name || "-";
                            return (
                                <span>
                                    {tableName} ({fromTable.field})
                                </span>
                            );
                        }
                        return <span>-</span>;
                    },
                },
                this.mode === "relation" && {
                    key: "to",
                    field: "to",
                    title: "To",
                    width: "",
                    align: "left",
                    renderBodyCell: ({ row }, h) => {
                        if (row.relation?.tableToTable) {
                            const toTable = row.relation.tableToTable.to;
                            const table = this.tables.find((t) => t._id === toTable.table);
                            const tableName = table?.name || "-";
                            const fieldName =
                                toTable.connectionType === "_id"
                                    ? "_id"
                                    : table?.fields?.find((f) => f._id === toTable.field)?.name || "-";
                            return (
                                <span>
                                    {tableName} ({fieldName})
                                </span>
                            );
                        } else if (row.relation?.standartToTable) {
                            const toTable = row.relation.standartToTable.to;
                            const table = this.tables.find((t) => t._id === toTable.table);
                            const tableName = table?.name || "-";
                            const fieldName =
                                toTable.connectionType === "_id"
                                    ? "_id"
                                    : table?.fields?.find((f) => f._id === toTable.field)?.name || "-";
                            return (
                                <span>
                                    {tableName} ({fieldName})
                                </span>
                            );
                        } else if (row.relation?.tableToStandart) {
                            const toStandart = row.relation.tableToStandart.to;
                            return (
                                <span>
                                    {toStandart.module} ({toStandart.field})
                                </span>
                            );
                        }
                        return <span>-</span>;
                    },
                },
                {
                    key: "description",
                    field: "description",
                    title: this.$t("modules.description"),
                    width: "200px",
                    align: "left",
                },
                this.mode === "module" && {
                    key: "module.settings.showInMainMenu",
                    field: "module.settings.showInMainMenu",
                    title: this.$t("modules.showInMainMenu"),
                    width: "",
                    align: "center",
                    renderBodyCell: ({ row }, h) => {
                        return <a-icon type={row.module?.settings?.showInMainMenu ? "check" : "close"} />;
                    },
                },
                this.mode === "module" && {
                    key: "module.settings.apiEnabled",
                    field: "module.settings.apiEnabled",
                    title: this.$t("modules.apiAccess"),
                    width: "",
                    align: "center",
                    renderBodyCell: ({ row }, h) => {
                        return <a-icon type={row.module?.settings?.apiEnabled ? "check" : "close"} />;
                    },
                },

                {
                    key: "actions",
                    field: "actions",
                    title: this.$t("modules.actions"),
                    width: "",
                    align: "center",
                    fixed: "right",
                    renderBodyCell: ({ row }, h) => {
                        const actions = [];
                        
                        // API Doc butonu (sadece module tipinde göster)
                        if (row.type === "module") {
                            actions.push(
                                h(SupsisButton, {
                                    props: {
                                        ghost: true,
                                        icon: "description",
                                        xsmall: true
                                    },
                                    attrs: {
                                        title: "API Doc"
                                    },
                                    on: {
                                        click: () => this.openModuleApiDoc(row)
                                    }
                                })
                            );
                        }
                        
                        if (PermissionController.check(["table.update"])) {
                            actions.push(
                                h(SupsisButton, {
                                    props: {
                                        ghost: true,
                                        icon: "edit",
                                        xsmall: true
                                    },
                                    on: {
                                        click: () => this.showEditModuleModal(row)
                                    }
                                })
                            );
                        }
                        
                        if (PermissionController.check(["table.delete"])) {
                            actions.push(
                                h(SupsisButton, {
                                    props: {
                                        danger: true,
                                        ghost: true,
                                        icon: "delete",
                                        xsmall: true
                                    },
                                    on: {
                                        click: () => this.deleteModule(row._id)
                                    }
                                })
                            );
                        }
                        
                        return h('div', { class: 'actions d-flex gap-5' }, actions);
                    },
                },
            ].filter(Boolean);
        },
        onSearch(value) {
            this.searchText = value;
        },
        filteredModules() {
            const modules = this.modules.filter((module) => module.type === this.mode);
            if (!this.searchText) {
                return modules;
            }
            if (this.searchText.trim().length === 0) {
                return modules;
            }
            return modules.filter(
                (module) =>
                    module.displayName.includes(this.searchText) ||
                    module.moduleName.includes(this.searchText) ||
                    module.apiName.includes(this.searchText),
            );
        },
        showCreateModuleModal() {
            this.isCreateModuleModalVisible = true;
        },
        hideCreateModuleModal() {
            this.isCreateModuleModalVisible = false;
        },
        async createModule() {
            await this.$refs.createModuleForm.handleSubmit();
        },
        onCreateModuleSuccess() {
            this.hideCreateModuleModal();
        },
        showEditModuleModal(module) {
            this.selectedModule = module;
            this.isEditModuleModalVisible = true;
        },
        hideEditModuleModal() {
            this.isEditModuleModalVisible = false;
            this.selectedModule = null;
        },
        async editModule() {
            await this.$refs.editModuleForm.handleSubmit();
        },
        onEditModuleSuccess() {
            this.hideEditModuleModal();
        },
        viewModule(module) {
            if (this.mode === "module") {
                this.$router.push(`/module/${module.apiName}`);
            } else {
                this.selectedModule = module;
                this.isEditModuleModalVisible = true;
            }
        },
        async deleteModule(moduleId) {
            try {
                await this.$confirm({
                    title: this.$t("modules.deleteModuleTitle"),
                    content: this.$t("modules.deleteModuleContent"),
                    okText: this.$t("calendar.yes"),
                    okType: "danger",
                    cancelText: this.$t("calendar.no"),
                    onOk: async () => {
                        await MODULE_API.deleteModule(moduleId);
                        this.syncModules();
                        this.$message.success(this.$t("modules.moduleDeletedSuccessfully"));
                    },
                });
            } catch (error) {
                console.error("Modül silinirken hata oluştu:", error);
            }
        },
        getApiDomain() {
            var domainMap = {
                production: "api.supsis.live",
                stage: "beta-api.supsis.live",
                development: "tablet-api-local.supsis.live",
                dev: "tablet-api-local.supsis.live",
            };
            var env = process.env.ENVIRONMENT || "production";
            return domainMap[env] || "api.supsis.live";
        },
        openModuleApiDoc(module) {
            var apiName = module.apiName || module.moduleName;
            var siteId = this.$store.state.site.site._id;
            var domain = this.getApiDomain();
            var url = "https://" + domain + "/api/module/" + apiName + "/swagger-ui?siteId=" + siteId;
            window.open(url, "_blank");
        },
    },
    mounted() {
        this.syncModules();
    },
    watch: {
        pagination: {
            handler(val, oldVal) {
                const pageSizeChanged = val.perPage !== oldVal.perPage;
                if (pageSizeChanged) {
                    this.pagination.page = 1;
                }
                this.syncModules();
            },
            deep: true,
        },
    },
});
</script>

<style scoped lang="scss">
.page-module-list {
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: 10px;
    width: 100%;
    overflow: auto;

    .title {
        font-size: 18px;
        font-weight: bold;
    }

    .table-wrapper {
        display: flex;
        flex-direction: column;
        gap: 5px;

        .toolbar {
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 10px 0;
            gap: 5px;

            .left {
                display: flex;
                gap: 5px;
                align-items: center;
            }

            .right {
                display: flex;
                gap: 5px;
                align-items: center;
            }
        }
    }
}

.actions {
    display: flex;
    gap: 5px;
}

.actions .supsis-button {
    flex-grow: 1;
    text-align: center;
}
</style>
<style lang="scss">
.module-name-link {
    color: rgb(0, 0, 0) !important;
    text-decoration: none;
    cursor: pointer;

    &:hover {
        text-decoration: underline !important;
    }
}
</style>
